import { useLocation, useOutlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "./Header.tsx";
import Footer from "./Footer.tsx";
import BackToTop from "./BackToTop.tsx";

/** カーテンの共通イージング（ゆったりした ease-in-out） */
const curtainEase = [0.45, 0, 0.25, 1] as const;

export default function Layout() {
  const location = useLocation();
  // AnimatePresence が退場中の旧ページを保持できるよう、Outlet を要素として取得する
  const outlet = useOutlet();

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      {/*
        key に location.key を使う。pathname だと同じページへのリンク
        （ロゴから「ホーム」など）でキーが変わらず遷移演出が走らないため。
      */}
      <AnimatePresence mode="wait" initial={false}>
        <div key={location.key} className="flex-grow flex flex-col">
          {/*
            遷移カーテン（観音扉ワイプ）:
            退場時はオレンジのパネルが左右から閉じて中央で合体し、
            入場時は左右へ開いて中央から新ページを見せる。
            幅は 50.5% にして中央に継ぎ目の隙間が出ないようにする。
          */}

          {/* 退場: 左パネル（薄いベールが濃くなりながら右へ閉じる） */}
          <motion.div
            className="fixed inset-y-0 left-0 w-[50.5%] z-[80] pointer-events-none backdrop-blur-sm bg-gradient-to-r from-ink via-ink/95 to-ink/85"
            initial={{ x: "-100%", opacity: 0.35 }}
            animate={{ x: "-100%", opacity: 0.35 }}
            exit={{ x: "0%", opacity: 1 }}
            transition={{ duration: 0.55, ease: curtainEase }}
          >
            <div className="absolute inset-y-0 right-0 w-28 bg-gradient-to-l from-brand-500/25 to-transparent" />
            <div className="absolute inset-y-0 right-0 w-px bg-brand-400/70" />
          </motion.div>
          {/* 退場: 右パネル（薄いベールが濃くなりながら左へ閉じる） */}
          <motion.div
            className="fixed inset-y-0 right-0 w-[50.5%] z-[80] pointer-events-none backdrop-blur-sm bg-gradient-to-l from-ink via-ink/95 to-ink/85"
            initial={{ x: "100%", opacity: 0.35 }}
            animate={{ x: "100%", opacity: 0.35 }}
            exit={{ x: "0%", opacity: 1 }}
            transition={{ duration: 0.55, ease: curtainEase }}
          >
            <div className="absolute inset-y-0 left-0 w-28 bg-gradient-to-r from-brand-500/25 to-transparent" />
            <div className="absolute inset-y-0 left-0 w-px bg-brand-400/70" />
          </motion.div>

          {/* 入場: 左パネル（透けながら左へ開く） */}
          <motion.div
            className="fixed inset-y-0 left-0 w-[50.5%] z-[81] pointer-events-none backdrop-blur-sm bg-gradient-to-r from-ink via-ink/95 to-ink/85"
            initial={{ x: "0%", opacity: 1 }}
            animate={{ x: "-100%", opacity: 0 }}
            exit={{ x: "-100%", opacity: 0 }}
            transition={{ duration: 0.75, ease: curtainEase, delay: 0.12 }}
          >
            <div className="absolute inset-y-0 right-0 w-28 bg-gradient-to-l from-brand-500/25 to-transparent" />
            <div className="absolute inset-y-0 right-0 w-px bg-brand-400/70" />
          </motion.div>
          {/* 入場: 右パネル（透けながら右へ開く） */}
          <motion.div
            className="fixed inset-y-0 right-0 w-[50.5%] z-[81] pointer-events-none backdrop-blur-sm bg-gradient-to-l from-ink via-ink/95 to-ink/85"
            initial={{ x: "0%", opacity: 1 }}
            animate={{ x: "100%", opacity: 0 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ duration: 0.75, ease: curtainEase, delay: 0.12 }}
          >
            <div className="absolute inset-y-0 left-0 w-28 bg-gradient-to-r from-brand-500/25 to-transparent" />
            <div className="absolute inset-y-0 left-0 w-px bg-brand-400/70" />
          </motion.div>

          {/* ページ本体はカーテンの下で軽くフェード */}
          <motion.main
            className="flex-grow bg-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.4, delay: 0.25 } }}
            exit={{ opacity: 1 }}
          >
            {outlet}
          </motion.main>
        </div>
      </AnimatePresence>
      <BackToTop />
      <Footer />
    </div>
  );
}
